import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API, API_BASE, API_PREFIX } from '../../constants';
import {
  PromptIdea,
  GetChatSessionsRequest,
  GetChatSessionMessagesRequest,
  DeleteChatSessionRequest,
  ChatSessionsResponse,
  ChatSessionResponse,
} from '../../../common/models/elsie-ai.model';
import { RootState } from '..';

interface AiGenericsResponse {
  conversationType: string;
  conversationId: string;
  citations: string[];
  citationOrdinals: Record<string, string>;
  totalTokens: number;
}

interface DataSource {
  id: string;
  name: string;
  enabled: boolean;
  isCollapsible?: boolean;
  elsieId?: string;
  items?: DataSource[];
}

interface DataSourcesResponse {
  dataSources: DataSource[];
}

const requestDefaults = {
  modelName: 'gpt-35-turbo-16k',
  deploymentName: 'gpt-35-turbo-16k',
};

/*
 * Prompt Ideas
 */
interface PromptIdeasRequest {
  conversationType: string;
  kwelloIds: string[];
  projectId: string;
  moduleId: number;
  searchIndexName: string;
}

interface PromptIdeasResponse {
  conversationType: string;
  promptIdeas: PromptIdea[];
  totalTokens: number;
}

/*
 * Coach Me
 */

interface CoachMeRequest {
  conversationType: string;
  userId: string;
  conversationId: string;
  kwelloIds: string[];
}

interface CoachMeResponse extends AiGenericsResponse {
  coachMe: {
    insightScore: string;
    overallFeedback: string;
    clarity: string;
    quality: string;
    relevance: string;
    consistency: string;
    depth: string;
    useOfEvidence: string;
    actionability: string;
    structure: string;
    brevity: string;
    objectivity: string;
  };
}

/*
 * Chat and Summarize
 */

export const chatRequestDefaults = {
  ...requestDefaults,
  projectSystemMessage: '',
  top: '1000',
  k: '50',
  temperature: '0.1',
};

interface BaseAiRequest {
  kwelloIds: string[];
  conversationId: string;
  userId: string;
  searchIndexName: string;
  projectId: string;
}

interface ChatRequest extends BaseAiRequest {
  question: string;
  filter?: Record<string, any>;
  projectClientName?: string;
  teamIds?: string[];
  dataSources?: string[];
  showQuery?: boolean;
  showBranch?: boolean;
}

export interface ChatResponse extends AiGenericsResponse {
  chatResponse: string;
  keyInsights: string[];
  // TODO: for the new api
  summary: string;
  citationOrdinals: Record<string, string>;
  citations: string[];
  conversationId: string;
  totalTokens: number;
  table: {
    data: { InteractionCount: number; Specialty: string }[];
    title: string;
  };
  visualization: {
    data: any;
    query: string;
    xAxis: string;
    yAxis: string;
    title: string;
  };
  error: string;
  time?: string;
  highlights: {
    conditions: string[];
    treatments: string[];
    medicalConcepts: string[];
    keywords: string[];
  };
}

interface FeedbackRequest {
  headers: Headers;
  payload: {
    conversationId: string;
    userId: string;
    rating: string;
    reason: string[];
    comments: string;
    freeText: string;
  };
}

export const elsieApi = createApi({
  reducerPath: 'elsieApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.accessToken;

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      return headers;
    },
  }),

  endpoints: (builder) => ({
    getCoachMe: builder.mutation<CoachMeResponse, CoachMeRequest>({
      query: (args: CoachMeRequest) => ({
        url: `${API_PREFIX.ELSIEAI}${API.COACH_ME}`,
        method: 'POST',
        body: JSON.stringify({ ...requestDefaults, ...args }),
      }),
    }),
    sendMessage: builder.mutation<ChatResponse, ChatRequest>({
      query: (args: ChatRequest) => ({
        url: `${API_PREFIX.ELSIEAI}${API.CHAT}`,
        method: 'POST',
        body: JSON.stringify({ ...args }),
      }),
    }),
    sendMessageV2: builder.mutation<ChatResponse, ChatRequest>({
      query: (args: ChatRequest) => ({
        url: `${API_PREFIX.ELSIEAIV2}${API.CHAT}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ ...args }),
      }),
    }),
    sendSummarize: builder.mutation<ChatResponse, BaseAiRequest>({
      query: (args: ChatRequest) => ({
        url: `${API_PREFIX.ELSIEAI}${API.SUMMARIZE}`,
        method: 'POST',
        body: JSON.stringify({ ...args, question: undefined }),
      }),
    }),
    sendSummarizeV2: builder.mutation<ChatResponse, ChatRequest>({
      query: (args: ChatRequest) => ({
        url: `${API_PREFIX.ELSIEAIV2}${API.CHAT}/${API.SUMMARIZE}`,
        headers: {
          projectId: args.projectId,
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        method: 'POST',
        body: JSON.stringify({
          conversationId: args.conversationId,
          kwelloIds: args.kwelloIds,
        }),
      }),
    }),
    getPromptIdeas: builder.mutation<PromptIdeasResponse, PromptIdeasRequest>({
      query: (args: PromptIdeasRequest) => ({
        url: `${API_PREFIX.ELSIEAI}${API.PROMPT_IDEAS}`,
        method: 'POST',
        body: JSON.stringify({ ...requestDefaults, ...args }),
      }),
    }),
    getPromptIdeasV2: builder.mutation<PromptIdeasResponse, PromptIdeasRequest>({
      query: (args: PromptIdeasRequest) => ({
        url: `${API_PREFIX.ELSIEAIV2}${API.CHAT}/${API.PROMPT_IDEAS}`,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ ...requestDefaults, ...args }),
      }),
    }),
    sendFeedback: builder.mutation<PromptIdeasResponse, FeedbackRequest>({
      query: (args: FeedbackRequest) => ({
        url: `${API_PREFIX.ELSIEAI}${API.FEEDBACK}`,
        headers: args.headers,
        method: 'POST',
        body: JSON.stringify({ ...requestDefaults, ...args.payload }),
      }),
    }),
    getDataSources: builder.query<
      DataSourcesResponse,
      { projectId: string; conversationId: string }
    >({
      query: ({ projectId, conversationId }) => ({
        url: `${API_PREFIX.ELSIEAIV2}${API.MULTI_COURCE}?conversationid=${conversationId}`,
        headers: {
          'Content-Type': 'application/json',
          projectId: projectId,
        },
        method: 'GET',
      }),
    }),
    uploadFiles: builder.mutation<
      any,
      { files: File[]; projectId: string; conversationId: string }
    >({
      query: ({ files, projectId, conversationId }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        const metadata = {
          DataType: 'ElsieAiUpload',
          SourceOrigin: 'ElsieAiUpload',
          ConversationId: conversationId,
        };
        formData.append('metadata', JSON.stringify(metadata));
        return {
          url: `${API_PREFIX.ELSIEAIV2}${API.FILE_UPLOAD}`,
          headers: {
            projectId,
          },
          method: 'POST',
          body: formData,
        };
      },
    }),

    // GET attached files by conversationId
    getAttachedFiles: builder.query<any, { conversationId?: string; projectId: string }>({
      query: ({ conversationId, projectId }) => ({
        url: `${API_PREFIX.ELSIEAIV2}${API.FILE_UPLOAD}?conversationId=${conversationId}`,
        headers: {
          projectId,
        },
        method: 'GET',
      }),
    }),

    /*
     * Chat History Endpoints
     */
    getChatSessions: builder.query<ChatSessionsResponse, GetChatSessionsRequest>({
      query: (args: GetChatSessionsRequest) => {
        const queryParams: string[] = [];
        if (args.count) queryParams.push(`count=${args.count}`);

        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

        return {
          url: `${API_PREFIX.ELSIEAIV2}${API.CHAT_HISTORY_SESSIONS}${queryString}`,
          headers: {
            'Content-Type': 'application/json',
            projectId: args.projectId,
            continuation_token: args.continuation_token,
          },
          method: 'GET',
        };
      },
    }),

    getChatSessionMessages: builder.query<ChatSessionResponse, GetChatSessionMessagesRequest>({
      query: (args: GetChatSessionMessagesRequest) => {
        const queryParams: string[] = [];
        if (args.count) queryParams.push(`count=${args.count}`);

        const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

        return {
          url: `${API_PREFIX.ELSIEAIV2}${API.CHAT_HISTORY_SESSIONS}/${args.sessionId}/messages${queryString}`,
          headers: {
            'Content-Type': 'application/json',
            projectId: args.projectId,
            continuation_token: args.continuation_token,
          },
          method: 'GET',
        };
      },
    }),

    deleteChatSession: builder.mutation<void, DeleteChatSessionRequest>({
      query: (args: DeleteChatSessionRequest) => ({
        url: `${API_PREFIX.ELSIEAIV2}${API.CHAT_HISTORY_SESSIONS}/${args.sessionId}`,
        headers: {
          'Content-Type': 'application/json',
          projectId: args.projectId,
        },
        method: 'DELETE',
      }),
    }),
    getFileUploadStatus: builder.query<
      any,
      { conversationId: string; referer: string; projectId: string }
    >({
      query: ({ conversationId, referer, projectId }) => ({
        url: `${API_PREFIX.ELSIEAIV2}${API.FILE_UPLOAD}/documents/status?conversationId=${conversationId}`,
        headers: {
          referer,
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          projectId,
        },
        method: 'GET',
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetDataSourcesQuery,
  useGetCoachMeMutation,
  useSendMessageMutation,
  useSendMessageV2Mutation,
  useSendSummarizeMutation,
  useSendSummarizeV2Mutation,
  useGetPromptIdeasMutation,
  useGetPromptIdeasV2Mutation,
  useSendFeedbackMutation,
  useUploadFilesMutation,
  useGetAttachedFilesQuery,
  useGetChatSessionsQuery,
  useGetChatSessionMessagesQuery,
  useDeleteChatSessionMutation,
  useLazyGetChatSessionsQuery,
  useLazyGetChatSessionMessagesQuery,
  useLazyGetFileUploadStatusQuery,
} = elsieApi;
